import { Type } from "class-transformer";
import { addDays, addMonths, addWeeks, addYears } from "date-fns";
import i18n from "../i18n";
import { AbstractJSONModel } from "./master-model";

export enum eUnit {
  year = 0,
  halfyear = 1,
  month = 2,
  weeks = 3
}

export interface INormPeriodType {
  id: number;
  name: string;
  institution_id: number | null;
  created_at: Date;
  updated_at: Date;
  unit_of_length: number;
  number_of_units: number;
  base_date: Date;
}

export interface INormPeriodTypePost {
  name: string;
  institution_id: number | null;
  unit_of_length: number;
  number_of_units: number;
  base_date: Date;
}

export interface INormPeriodTypePut {
  id: number;
  name: string;
  unit_of_length: number;
  number_of_units: number;
  base_date: Date;
}

export class NormPeriodType extends AbstractJSONModel implements INormPeriodType {
  public id = -1;
  public name = "";
  @Type(() => Date)
  public base_date: Date = new Date();
  @Type(() => Date)
  public created_at: Date = new Date();
  public institution_id: number | null = null;
  public number_of_units = -1;
  public unit_of_length = -1;
  @Type(() => Date)
  public updated_at: Date = new Date();

  public get startDate(): Date {
    let startDate = this.base_date;
    if (startDate > new Date()) {
      return startDate;
    }
    if (this.unit_of_length === eUnit.year) {
      while (new Date() >= startDate) {
        startDate = addYears(startDate, 1 * this.number_of_units);
      }
      return addYears(startDate, -1 * this.number_of_units);
    } else if (this.unit_of_length === eUnit.halfyear) {
      while (new Date() >= startDate) {
        startDate = addMonths(startDate, 6 * this.number_of_units);
      }
      return addMonths(startDate, -6 * this.number_of_units);
    } else if (this.unit_of_length === eUnit.month) {
      while (new Date() >= startDate) {
        startDate = addMonths(startDate, 1 * this.number_of_units);
      }
      return addMonths(startDate, -1 * this.number_of_units);
    } else if (this.unit_of_length === eUnit.weeks) {
      while (new Date() >= startDate) {
        startDate = addWeeks(startDate, 1 * this.number_of_units);
      }
      return addWeeks(startDate, -1 * this.number_of_units);
    } else {
      return this.base_date;
    }
  }

  public get endDate(): Date {
    if (this.unit_of_length === eUnit.year) {
      return addDays(addYears(this.startDate, 1 * this.number_of_units), -1);
    } else if (this.unit_of_length === eUnit.halfyear) {
      return addDays(addMonths(this.startDate, 6 * this.number_of_units), -1);
    } else if (this.unit_of_length === eUnit.month) {
      return addDays(addMonths(this.startDate, 1 * this.number_of_units), -1);
    } else if (this.unit_of_length === eUnit.weeks) {
      return addDays(addWeeks(this.startDate, 1 * this.number_of_units), -1);
    } else {
      return this.base_date;
    }
  }

  public get unit() {
    return i18n.global.t(eUnit[this.unit_of_length]).toString().toLowerCase();
  }

  public convertToPostObject(): INormPeriodTypePost {
    const n: INormPeriodTypePost = Object.assign({});
    n.base_date = this.base_date;
    n.institution_id = this.institution_id;
    n.name = this.name;
    n.number_of_units = this.number_of_units;
    n.unit_of_length = this.unit_of_length;
    return n;
  }

  public convertToPutObject(): INormPeriodTypePut {
    const n: INormPeriodTypePut = Object.assign({});
    n.id = this.id;
    n.base_date = this.base_date;
    n.name = this.name;
    n.number_of_units = this.number_of_units;
    n.unit_of_length = this.unit_of_length;
    return n;
  }

  equals(other: NormPeriodType): boolean {
    return other.id === this.id;
  }
}
